import { TrendingUp, Shield, Award } from "lucide-react";
import { Button } from "./ui/button";

import heroLogo from "@/assets/hero-logo.png";
import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useState, useRef } from "react";
const TypewriterText = ({
  text
}: {
  text: string;
}) => {
  const [displayedText, setDisplayedText] = useState("");
  const [showCursor, setShowCursor] = useState(true);
  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= text.length) {
        setDisplayedText(text.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
        setTimeout(() => setShowCursor(false), 300);
      }
    }, 35); // Much faster typing speed

    return () => clearInterval(interval);
  }, [text]);
  return <span>
      {displayedText}
      {showCursor && <span className="animate-pulse">|</span>}
    </span>;
};
const Hero = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const backgroundScale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);
  const backgroundOpacity = useTransform(scrollYProgress, [0, 0.8], [0.08, 0]);
  const handleRegisterClick = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handleTestimonialsClick = () => {
    document.getElementById('testimonials')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section ref={sectionRef} className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background">
      {/* Animated orbital glow accents */}
      <motion.div
        className="absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-primary/10 blur-3xl pointer-events-none"
        animate={{ x: [0, 60, 0], y: [0, -40, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full bg-accent/15 blur-3xl pointer-events-none"
        animate={{ x: [0, -60, 0], y: [0, 40, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Background Logo with Parallax Animation */}
      <motion.div className="absolute inset-0 flex items-center justify-center" initial={{
      opacity: 0,
      scale: 0.8
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.5,
      ease: "easeOut"
    }} style={{
      y: backgroundY
    }}>
        <motion.img src={heroLogo} alt="Fitawrari FX Logo" className="w-[600px] h-[600px] md:w-[800px] md:h-[800px] object-contain dark:invert dark:brightness-200 dark:contrast-100 transition-[filter]" loading="eager" decoding="async" fetchPriority="high" style={{
        scale: backgroundScale,
        opacity: backgroundOpacity
      }} animate={{
        scale: [1, 1.02, 1]
      }} transition={{
        duration: 4,
        ease: "easeInOut",
        repeat: Infinity,
        repeatType: "reverse"
      }} />
      </motion.div>
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center pt-24">
        {/* SEO H1 — visually hidden, semantically primary for indexing */}
        <h1 className="sr-only">Fitawrari FX — Forex Mentorship & Trading Education in Ethiopia</h1>

        {/* Visual headline with Welcome Animation */}
        <motion.div className="font-display text-5xl md:text-7xl font-bold mb-4" initial={{
        opacity: 0,
        y: -30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        ease: "easeOut"
      }} aria-hidden="true">
          <span className="inline-block">Fitawrari</span>{" "}
          <span className="inline-block text-primary">FX</span>
        </motion.div>
        
        {/* Motto - Typewriter Effect - starts immediately */}
        <p className="text-gradient text-2xl md:text-3xl font-bold mb-6">
          <TypewriterText text="Leading the Front, Rising Together" />
        </p>
        
        {/* Welcome Note */}
        <motion.p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto mb-6" initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 1.2
      }}>
          Welcome to Fitawrari FX. We provide the strategy, the tools, and the trust you need to master the Forex markets. Stop chasing trends and start leading them.
        </motion.p>
        
        {/* CTAs */}
        <motion.div className="flex flex-col items-center gap-4 mb-4" initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 1.4
      }}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="glow animate-pulse-glow" onClick={handleRegisterClick}>
              Register Now
            </Button>
            
            <Button size="lg" variant="outline" asChild>
              <a href="#pricing">View Pricing</a>
            </Button>
          </div>
        </motion.div>

        {/* Limited Spots Badge */}
        <motion.div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-4" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        delay: 1.5
      }}>
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-sm text-muted-foreground">Limited Spots Available</span>
        </motion.div>
        
        {/* Subheadline - Learn SK System */}
        <motion.p className="text-lg md:text-xl text-foreground/80 max-w-2xl mx-auto mb-16" initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 1.6
      }}>
          Learn SK System Strategies, Volume Confirmation & Advanced Risk Management from Fitawrari FX
        </motion.p>
        
        {/* Stats */}
        <motion.div className="flex flex-wrap items-center justify-center gap-8 md:gap-16" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 1.7
      }}>
          <motion.div className="flex flex-col items-center gap-2" whileHover={{
          scale: 1.1,
          y: -5
        }} transition={{
          duration: 0.3
        }}>
            <TrendingUp className="w-6 h-6 text-primary mb-1" />
            <span className="text-2xl font-bold text-foreground">4+</span>
            <span className="text-sm text-muted-foreground">Years Experience</span>
          </motion.div>
          <motion.div className="flex flex-col items-center gap-2 cursor-pointer" whileHover={{
          scale: 1.1,
          y: -5
        }} whileTap={{
          scale: 0.95
        }} transition={{
          duration: 0.3
        }} onClick={handleTestimonialsClick}>
            <Shield className="w-6 h-6 text-primary mb-1" />
            <span className="text-2xl font-bold text-foreground">✓</span>
            <span className="text-sm text-muted-foreground hover:text-primary transition-colors text-center">Proven Track Record</span>
          </motion.div>
          <motion.div className="flex flex-col items-center gap-2" whileHover={{
          scale: 1.1,
          y: -5
        }} transition={{
          duration: 0.3
        }}>
            <Award className="w-6 h-6 text-primary mb-1" />
            <span className="text-2xl font-bold text-foreground">SK</span>
            <span className="text-sm text-muted-foreground">Unique Methodology</span>
          </motion.div>
        </motion.div>
      </div>
    </section>;
};
export default Hero;